import CircularProgress from '@mui/material/CircularProgress';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import classNames from 'classnames';
import { ReactElement, useRef, useState } from 'react';
import { FileDrop } from 'react-file-drop';

import { formatUploaded } from '~/utils/date/date';
import { IResource } from '~/services/api/resource/types';
import editIcon from '~/ui/assets/images/editOutline.svg';
import previewIcon from '~/ui/assets/images/previewOutline.svg';
import deleteIcon from '~/ui/assets/images/trashOutline.svg';
import uploadIcon from '~/ui/assets/images/undraw.svg';
import { CE_DOCUMENT_ACCEPT } from '~/ui/constants/communityEngagement';
import FileViewer from '~/ui/pages/Resources/components/FileViewer';
import getFileTypeIcon from '~/ui/pages/Resources/helpers/getFileTypeIcon';

import CeDocumentStatus from '~/ui/pages/Clients/reusable/CommunityEngagement/constants/CeDocumentStatus';
import { getTypeByFileName } from '~/ui/pages/Clients/reusable/CommunityEngagement/helpers/formatCeDocument';
import { ICeDocuments } from '~/ui/pages/Clients/reusable/CommunityEngagement/hooks/useCeDocuments';
import {
  isSavableCeDocument,
  toCeDocumentPreview,
} from '~/ui/pages/Clients/reusable/CommunityEngagement/utils/ceDocumentItem';
import CeDocumentNameEditor from '../CeDocumentNameEditor';

import styles from './CeDocumentsField.module.scss';

interface ICeDocumentsFieldProps {
  documents: ICeDocuments;
}

const CeDocumentsField = ({
  documents: {
    items,
    errors,
    isFull,
    renamingKey,
    addFiles,
    remove,
    startRename,
    cancelRename,
    getRenameError,
    rename,
  },
}: ICeDocumentsFieldProps): ReactElement => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<IResource | null>(null);

  const onFiles = (files: FileList | null) => {
    if (!files?.length || isFull) return;
    addFiles(Array.from(files));
  };

  return (
    <div className={styles.field}>
      <FileDrop
        onDrop={(files, event) => {
          event.preventDefault();
          onFiles(files);
        }}
        className={styles.dropzone}
        targetClassName={styles.dropzoneTarget}
        draggingOverTargetClassName={classNames({ [styles.draggingOver]: !isFull })}
      >
        <button
          type="button"
          className={styles.dropzoneButton}
          disabled={isFull}
          onClick={() => inputRef.current?.click()}
        >
          <img src={uploadIcon} alt="" />
          <span>
            <span className={styles.dropzoneTitle}>Upload File</span>
            <span className={styles.dropzoneDescription}>
              Drag and drop files or browse your computer
            </span>
          </span>
        </button>
      </FileDrop>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={CE_DOCUMENT_ACCEPT}
        aria-label="Upload documents"
        disabled={isFull}
        className={styles.hidden}
        onChange={event => {
          onFiles(event.target.files);
          event.target.value = '';
        }}
      />
      {errors.map(error => (
        <FormHelperText key={error} error>
          {error}
        </FormHelperText>
      ))}
      {items.length > 0 && (
        <ul className={styles.list}>
          {items.map(item => {
            const isRenaming = item.key === renamingKey;
            return (
              <li
                key={item.key}
                className={classNames(styles.card, {
                  [styles.failed]: item.status === CeDocumentStatus.Failed,
                  [styles.renaming]: isRenaming,
                })}
              >
                <span className={styles.icon}>
                  {getFileTypeIcon(getTypeByFileName(item.fileName))}
                </span>
                {isRenaming ? (
                  <CeDocumentNameEditor
                    fileName={item.fileName}
                    getError={getRenameError}
                    onSave={fileName => rename(item.key, fileName)}
                    onCancel={cancelRename}
                  />
                ) : (
                  <span className={styles.details}>
                    <span className={styles.name}>{item.fileName}</span>
                    {item.status === CeDocumentStatus.Existing && (
                      <span className={styles.meta}>{formatUploaded(item.document)}</span>
                    )}
                    {item.status === CeDocumentStatus.Failed && (
                      <span className={styles.error}>{item.error}</span>
                    )}
                  </span>
                )}
                {item.status === CeDocumentStatus.Uploading && (
                  <CircularProgress size={20} aria-label={`Uploading ${item.fileName}`} />
                )}
                {!isRenaming && item.status !== CeDocumentStatus.Uploading && (
                  <span className={styles.actions}>
                    {item.status === CeDocumentStatus.Uploaded && (
                      <IconButton
                        aria-label={`Rename ${item.fileName}`}
                        disabled={renamingKey !== null}
                        onClick={() => startRename(item.key)}
                      >
                        <img src={editIcon} alt="Rename" />
                      </IconButton>
                    )}
                    {isSavableCeDocument(item) && (
                      <IconButton
                        aria-label={`Preview ${item.fileName}`}
                        onClick={() => setPreview(toCeDocumentPreview(item))}
                      >
                        <img src={previewIcon} alt="Preview" />
                      </IconButton>
                    )}
                    <IconButton
                      aria-label={`Remove ${item.fileName}`}
                      onClick={() => remove(item.key)}
                    >
                      <img src={deleteIcon} alt="Remove" />
                    </IconButton>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {preview && <FileViewer file={preview} onClose={() => setPreview(null)} />}
    </div>
  );
};

export default CeDocumentsField;
